import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box, Typography, Button, Avatar, Chip, CircularProgress, Grid,
  Card, CardContent, CardHeader, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Drawer,
} from "@mui/material";
import {
  ArrowBack, Edit, Business, Phone, Email, Language, LinkedIn,
  People, LocationOn, Add, DoubleArrow,
} from "@mui/icons-material";
import { customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  childBoxStyleSpaceBetween,
  doubleArrowStyle,
  drawerStyle,
  parentBoxStyle,
  themes,
} from "services/constants";
import CompanyFormPage from "./CompanyFormPage";
import ContactFormPage from "./ContactFormPage";

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
      {React.cloneElement(icon, { sx: { color: "text.secondary", mt: 0.2, fontSize: 18 } })}
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2">{value}</Typography>
      </Box>
    </Box>
  );
}

function stringToColor(str = "") {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 50%, 45%)`;
}

const contactDrawerStyle = {
  ...drawerStyle,
  "& .MuiDrawer-paper": {
    ...drawerStyle["& .MuiDrawer-paper"],
    width: { xs: "100vw", sm: "760px" },
    maxWidth: "100vw",
  },
};

export default function CompanyDetail() {
  const history = useHistory();
  const { id } = useParams();
  const { domain } = useSelector((s) => s.tokenReducer || {});

  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await customGetAPI(`${domain}crm/companies/${id}/`);
      setCompany(res.responseDetails);
    } catch {
      toast.error("Failed to load account");
    } finally {
      setLoading(false);
    }
  }, [domain, id]);

  const loadContacts = useCallback(async () => {
    try {
      const res = await customGetAPI(`${domain}crm/contacts/?company_fk=${id}&page_size=100`);
      const data = res.responseDetails;
      setContacts(data?.results ?? data ?? []);
    } catch {
      toast.error("Failed to load contacts");
    } finally {
      setContactsLoading(false);
    }
  }, [domain, id]);

  useEffect(() => { load(); loadContacts(); }, [load, loadContacts]);

  const handleContactSaved = useCallback(async () => {
    await loadContacts();
    setContactDrawerOpen(false);
  }, [loadContacts]);

  const handleCompanySaved = useCallback(async () => {
    await load();
    setCompanyDrawerOpen(false);
  }, [load]);

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  if (!company) return <Box p={3}><Typography>Account not found.</Typography></Box>;

  const address = [
    company.address_street,
    company.address_city,
    company.address_state,
    company.address_postal_code,
    company.address_country,
  ].filter(Boolean).join(", ");

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Button startIcon={<ArrowBack />} onClick={() => history.push("/admin/crm/companies")} size="small">Back</Button>
          <Avatar sx={{ bgcolor: stringToColor(company.name), width: 48, height: 48, fontSize: 20 }}>
            {company.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>{company.name}</Typography>
            {company.industry && <Chip label={company.industry} size="small" sx={{ mt: 0.5 }} />}
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => setCompanyDrawerOpen(true)}
        >
          Edit
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Account Info */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardHeader
              title="Account Details"
              titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
            />
            <CardContent>
              <InfoRow icon={<Business />} label="Industry" value={company.industry} />
              <InfoRow icon={<People />} label="Employees" value={company.employee_count?.toString()} />
              <InfoRow icon={<Phone />} label="Phone" value={company.phone} />
              <InfoRow icon={<Email />} label="Email" value={company.email} />
              <InfoRow icon={<Language />} label="Website" value={company.website} />
              <InfoRow icon={<LinkedIn />} label="LinkedIn" value={company.linkedin} />
              <InfoRow icon={<LocationOn />} label="Address" value={address} />
              {company.assigned_to_name && (
                <InfoRow icon={<People />} label="Assigned Agent" value={company.assigned_to_name} />
              )}
              {company.notes && (
                <Box mt={1}>
                  <Typography variant="caption" color="text.secondary" display="block">Notes</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{company.notes}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Contacts */}
        <Grid item xs={12} md={8}>
          <Card variant="outlined">
            <CardHeader
              title={
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    <People fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={600}>Contacts</Typography>
                    <Chip label={contacts.length} size="small" color="primary" />
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setContactDrawerOpen(true)}
                  >
                    Add Contact
                  </Button>
                </Box>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {contactsLoading ? (
                <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
              ) : contacts.length === 0 ? (
                <Box textAlign="center" p={3}>
                  <Typography color="text.secondary">No contacts linked to this account yet.</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Position</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Agent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contacts.map((c) => (
                        <TableRow
                          key={c.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => history.push(`/admin/crm/contacts?contact_id=${c.id}`)}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar sx={{ width: 28, height: 28, bgcolor: stringToColor(`${c.first_name}${c.last_name}`), fontSize: 12 }}>
                                {c.first_name?.[0]?.toUpperCase()}
                              </Avatar>
                              <Typography variant="body2">{c.first_name} {c.last_name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{c.position || "—"}</TableCell>
                          <TableCell>
                            {c.status_name && (
                              <Chip
                                label={c.status_name}
                                size="small"
                                sx={{ bgcolor: c.status_color || "grey.200", color: "#fff", fontSize: 11 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{c.username || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Drawer
        anchor="right"
        open={contactDrawerOpen}
        onClose={() => setContactDrawerOpen(false)}
        sx={contactDrawerStyle}
      >
        <Box sx={parentBoxStyle}>
          <Paper
            elevation={0}
            sx={{
              borderBottom: `1px solid ${themes?.miliedLightGray || "#e0e0e0"}`,
              borderRadius: 0,
            }}
          >
            <Box sx={childBoxStyleSpaceBetween}>
              <IconButton onClick={() => setContactDrawerOpen(false)} sx={doubleArrowStyle}>
                <DoubleArrow />
              </IconButton>
            </Box>
            <Box sx={{ ml: 2.2, mr: 2, mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Add Contact
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a contact linked to {company.name}.
              </Typography>
            </Box>
          </Paper>
          <Box sx={{ flexGrow: 1, overflow: "auto", p: 2, pb: 0 }}>
            <ContactFormPage
              key={`company-contact-${id}`}
              drawerMode
              hideHeader
              prefillCompany={{ id: Number(id), name: company.name }}
              onClose={() => setContactDrawerOpen(false)}
              onSaved={handleContactSaved}
            />
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={companyDrawerOpen}
        onClose={() => setCompanyDrawerOpen(false)}
        sx={contactDrawerStyle}
      >
        <Box sx={parentBoxStyle}>
          <Paper
            elevation={0}
            sx={{
              borderBottom: `1px solid ${themes?.miliedLightGray || "#e0e0e0"}`,
              borderRadius: 0,
            }}
          >
            <Box sx={childBoxStyleSpaceBetween}>
              <IconButton onClick={() => setCompanyDrawerOpen(false)} sx={doubleArrowStyle}>
                <DoubleArrow />
              </IconButton>
            </Box>
            <Box sx={{ ml: 2.2, mr: 2, mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Edit Account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Update account details, contact channels, and address.
              </Typography>
            </Box>
          </Paper>
          <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
            <CompanyFormPage
              key={`company-edit-${id}`}
              companyId={id}
              drawerMode
              hideHeader
              onClose={() => setCompanyDrawerOpen(false)}
              onSaved={handleCompanySaved}
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
